import { useState, useCallback } from 'react';
import axios from 'axios';
import { BASE_URL, apiCall } from '../../api/apiClient';

const TB = `${BASE_URL}/tournament-brackets`;

export function useBracket(event) {
  const [bracket, setBracket] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!event?._id) return;
    setLoading(true);
    setError('');
    try {
      const res = await apiCall(h => axios.get(`${TB}?eventId=${event._id}`, { headers: h }));
      const list = res.data?.data || [];
      setBracket(list[0] || null);
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to load bracket');
    } finally {
      setLoading(false);
    }
  }, [event?._id]);

  const createBracket = async (payload) => {
    const res = await apiCall(h => axios.post(TB, payload, { headers: h }));
    setBracket(res.data.data);
    return res.data.data;
  };

  const updateBracket = async (id, payload) => {
    const res = await apiCall(h => axios.put(`${TB}/${id}`, payload, { headers: h }));
    setBracket(res.data.data);
    return res.data.data;
  };

  const deleteBracket = async (id) => {
    await apiCall(h => axios.delete(`${TB}/${id}`, { headers: h }));
    setBracket(null);
  };

  // Stages
  const addStage = async (bracketId, payload) => {
    const res = await apiCall(h => axios.post(`${TB}/${bracketId}/stages`, payload, { headers: h }));
    setBracket(res.data.data);
    return res.data.data;
  };

  const updateStage = async (bracketId, stageId, payload) => {
    const res = await apiCall(h => axios.put(`${TB}/${bracketId}/stages/${stageId}`, payload, { headers: h }));
    setBracket(res.data.data);
    return res.data.data;
  };

  const removeStage = async (bracketId, stageId) => {
    const res = await apiCall(h => axios.delete(`${TB}/${bracketId}/stages/${stageId}`, { headers: h }));
    setBracket(res.data.data);
  };

  // Games in a stage
  const addGameEntry = async (bracketId, stageId, payload) => {
    const res = await apiCall(h => axios.post(`${TB}/${bracketId}/stages/${stageId}/games`, payload, { headers: h }));
    setBracket(res.data.data);
    return res.data.data;
  };

  const updateGameEntry = async (bracketId, stageId, gameEntryId, payload) => {
    const res = await apiCall(h => axios.put(`${TB}/${bracketId}/stages/${stageId}/games/${gameEntryId}`, payload, { headers: h }));
    setBracket(res.data.data);
    return res.data.data;
  };

  const removeGameEntry = async (bracketId, stageId, gameEntryId) => {
    const res = await apiCall(h => axios.delete(`${TB}/${bracketId}/stages/${stageId}/games/${gameEntryId}`, { headers: h }));
    setBracket(res.data.data);
  };

  // Links
  const patchLink = async (bracketId, stageId, gameEntryId, linkPayload) => {
    const res = await apiCall(h => axios.patch(`${TB}/${bracketId}/stages/${stageId}/games/${gameEntryId}/link`, linkPayload, { headers: h }));
    setBracket(res.data.data);
    return res.data.data;
  };

  // Create real game at /games
  const createGame = async (payload) => {
    const res = await apiCall(h => axios.post(`${BASE_URL}/games`, payload, { headers: h }));
    return res.data?.data || res.data;
  };

  return {
    bracket, loading, error,
    load, createBracket, updateBracket, deleteBracket,
    addStage, updateStage, removeStage,
    addGameEntry, updateGameEntry, removeGameEntry,
    patchLink, createGame,
    setBracket,
  };
}
